import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { companyNameSchema } from '../lib/validators'
import { toast } from 'sonner'
import {
  Archive,
  Copy,
  Mail,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { RowActionsMenu } from './table/RowActionsMenu'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { Select } from './ui/Select'
import { api, extractApiError } from '../lib/api'
import { CountryFilter } from './ui/CountryFilter'
import { plansResponse } from '../lib/types'
import { copyText } from '../lib/clipboard'

/**
 * Platform Admin row actions for a tenant — renders as a horizontal
 * strip of icon buttons in the row's Actions cell. Matches the
 * Workspaces page (Company Admin) pattern so both admin tables share
 * one grammar for "row mutations".
 *
 * Status drives which suspend/reactivate icon is visible — the other
 * is hidden so you can't pick a nonsensical transition. The backend
 * also rejects bad ones, but gating in the UI removes a class of
 * confusing error toasts.
 */
const editSchema = z.object({
  // The SHARED validator, same as Sign Up and the Create Company dialog.
  // This was a hand-rolled copy capped at 200 with no format rule, so Edit
  // accepted names Create would reject — a 200-character name, and an
  // all-digits one like "00000000000" (QA-reported). One field, one rule.
  name: companyNameSchema('Company Name'),
  // Plan code — validated against the live Plan catalogue server-side.
  // Required, and now marked with a `*` to match the Create dialog.
  // Already effectively required before: `.optional()` only ever admitted
  // `undefined`, and the form always supplies a string, so a NO-PLAN
  // tenant's '' failed `.min(1)` with Zod's default "Too small" text. The
  // messages below just make that failure say which field and why.
  planTier: z
    .string({ message: 'Plan is required.' })
    .trim()
    .min(1, 'Plan is required.')
    .max(50)
    .regex(/^[a-z0-9_]+$/, 'Plan is required.'),
  // Company location — ISO 3166-1 alpha-2. Optional here (unlike the
  // required create-time field): an empty value is allowed so a legacy
  // company with no country can still be renamed without being forced to
  // set one. Picking a country corrects/sets it.
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Please select a valid Country.')
    .or(z.literal(''))
    .optional(),
})

export function TenantRowActions({ tenant }) {
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  // Dedicated "Manage Plan" dialog — a focused, discoverable action in
  // the row's Actions strip (plan is also editable inside Edit, but a
  // standalone action is the faster, more obvious path the platform
  // admin reaches for). `planTierDraft` holds the in-flight selection.
  const [planOpen, setPlanOpen] = useState(false)
  const [planTierDraft, setPlanTierDraft] = useState(tenant.planTier ?? '')
  // Extra confirmation for a downgrade (moving to a cheaper / trial /
  // lower-cap plan). Bug 0013115: the dialog let a Super Admin move an
  // Enterprise tenant onto Free Trial in one click with no warning,
  // silently shrinking users / storage / features. We now require a
  // deliberate checkbox tick before Save enables.
  const [confirmDowngrade, setConfirmDowngrade] = useState(false)
  const [confirmSuspend, setConfirmSuspend] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')
  const [confirmReactivate, setConfirmReactivate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Offboarding is TERMINAL — the company is archived, members are
  // signed out, and the row moves to the Offboarded filter bucket.
  // Bug 0013081: filter dropdown listed "Offboarded" but no action
  // could set that status, so the bucket was permanently empty. The
  // reason is shown to locked-out members on the sign-in screen
  // alongside the support contact — same pattern as Suspend.
  const [confirmOffboard, setConfirmOffboard] = useState(false)
  const [offboardReason, setOffboardReason] = useState('')
  // Resend-invite is a one-click action — no confirm dialog, but we
  // surface the returned setupUrl in a tiny follow-up modal so the
  // admin can copy-paste it as a fallback to the email.
  const [inviteResult, setInviteResult] = useState(null)

  const editForm = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: tenant.name,
      planTier: tenant.planTier ?? '',
      country: tenant.country ?? '',
    },
  })
  useEffect(() => {
    if (editOpen) {
      editForm.reset({
        name: tenant.name,
        planTier: tenant.planTier ?? '',
        country: tenant.country ?? '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, tenant.name, tenant.planTier, tenant.country])

  // Open the Manage Plan dialog, seeding the draft to the tenant's
  // current plan so a cancelled change never leaks into the next open.
  // Seeding here (rather than in an effect) keeps the reset out of the
  // render/effect cycle.
  function openManagePlan() {
    setPlanTierDraft(tenant.planTier ?? '')
    setPlanOpen(true)
  }

  // Plan catalogue for the Plan dropdown — loaded only when a dialog that
  // needs it (Edit or Manage Plan) opens. Includes private plans (admin
  // can assign them here) but skips inactive ones (no scenario where
  // you'd move a tenant onto a hidden plan).
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    enabled: editOpen || planOpen,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })
  // Ranking helper — turns a plan into a single comparable "tier score"
  // for downgrade detection. Trial < Paid-with-numeric-price <
  // Enterprise (custom-priced paid). The exact numbers don't matter,
  // only the ordering: any move to a strictly lower score is treated
  // as a downgrade and prompts an extra confirmation.
  function planScore(p) {
    if (!p) return 0
    if (p.type === 'trial') return 0
    const raw = p.monthlyPrice
    if (raw == null || raw === '') return Number.POSITIVE_INFINITY // Enterprise / custom
    const n = Number(raw)
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
  }
  // Resolve the tenant's current plan document + the draft. Both come
  // from the same catalogue query so type / monthlyPrice fields stay
  // in sync with what the plan card shows.
  const currentPlan = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return list.find((p) => p.code === tenant.planTier) ?? null
  }, [plansQuery.data, tenant.planTier])
  const draftPlan = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return list.find((p) => p.code === planTierDraft) ?? null
  }, [plansQuery.data, planTierDraft])
  const isDowngrade =
    !!currentPlan &&
    !!draftPlan &&
    currentPlan.code !== draftPlan.code &&
    planScore(draftPlan) < planScore(currentPlan)
  // Reset the confirmation any time the draft changes OR the dialog
  // reopens — a fresh open should never come pre-armed to save.
  useEffect(() => {
    setConfirmDowngrade(false)
  }, [planTierDraft, planOpen])

  const planOptions = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return [...list]
      .filter((p) => p.isActive)
      .sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      )
      .map((p) => ({
        value: p.code,
        label: p.name + (p.isPublic === false ? ' (Private)' : ''),
      }))
  }, [plansQuery.data])

  function invalidateTenants() {
    void qc.invalidateQueries({ queryKey: ['platform-tenants'] })
    void qc.invalidateQueries({ queryKey: ['platform-tenant', tenant.id] })
    void qc.invalidateQueries({ queryKey: ['platform-overview'] })
  }

  const editMutation = useMutation({
    mutationFn: async (v) => {
      const res = await api.patch(`/super-admin/tenants/${tenant.id}`, {
        name: v.name.trim(),
        // Only send planTier when the admin actually changed it — keeps
        // the audit log entry tight and avoids a no-op feature-blob
        // re-sync on every save.
        ...(v.planTier && v.planTier !== tenant.planTier
          ? { planTier: v.planTier }
          : {}),
        // Likewise only send country when it changed (compare against the
        // normalised current value). Sending '' clears it back to "not set".
        ...((v.country ?? '') !== (tenant.country ?? '')
          ? { country: v.country ?? '' }
          : {}),
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Company updated successfully.')
      setEditOpen(false)
      invalidateTenants()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) editForm.setError('name', { message: fields.name })
      else if (fields?.planTier)
        editForm.setError('planTier', { message: fields.planTier })
      else if (fields?.country)
        editForm.setError('country', { message: fields.country })
      else toast.error(message)
    },
  })

  const planMutation = useMutation({
    mutationFn: async () => {
      const res = await api.patch(`/super-admin/tenants/${tenant.id}`, {
        planTier: planTierDraft,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success(`Plan updated for "${tenant.name}".`)
      setPlanOpen(false)
      invalidateTenants()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const suspendMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/super-admin/tenants/${tenant.id}/status`, {
        status: 'suspended',
        // Shown to locked-out members on the sign-in screen alongside
        // the support contact, so they understand WHY.
        reason: suspendReason.trim() || undefined,
      })
    },
    onSuccess: () => {
      toast.success(`"${tenant.name}" suspended.`)
      setConfirmSuspend(false)
      setSuspendReason('')
      invalidateTenants()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/super-admin/tenants/${tenant.id}/status`, {
        status: 'active',
      })
    },
    onSuccess: () => {
      toast.success(`"${tenant.name}" reactivated.`)
      setConfirmReactivate(false)
      invalidateTenants()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const offboardMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/super-admin/tenants/${tenant.id}/status`, {
        status: 'offboarded',
        reason: offboardReason.trim() || undefined,
      })
    },
    onSuccess: () => {
      toast.success(`"${tenant.name}" offboarded.`)
      setConfirmOffboard(false)
      setOffboardReason('')
      invalidateTenants()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/super-admin/tenants/${tenant.id}`)
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? `"${tenant.name}" deleted successfully.`)
      setConfirmDelete(false)
      invalidateTenants()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const resendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/super-admin/tenants/${tenant.id}/resend-invite`,
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? 'Invitation re-sent.')
      setInviteResult(data)
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const isActive = tenant.status === 'active'
  // Resend invite only makes sense while the tenant's first Company
  // Admin is still in `invited` status. The list payload surfaces
  // this via pendingInviteCount — older responses without the field
  // default to 0 so the icon hides safely.
  const hasPendingInvite = (tenant.pendingInviteCount ?? 0) > 0

  return (
    <>
      {/* Row actions as a kebab (⋮) menu — the DataTable standard. Same
       *  actions the old icon strip had, status-gated identically (Suspend
       *  vs Reactivate, Offboard hidden once offboarded, Resend only while a
       *  pending invite exists); Delete sits below a separator, in red. */}
      <RowActionsMenu
        label={`Actions for ${tenant.name}`}
        items={[
          {
            key: 'edit',
            label: 'Edit',
            icon: Pencil,
            onClick: () => setEditOpen(true),
          },
          {
            key: 'plan',
            label: 'Manage plan',
            icon: RefreshCw,
            onClick: openManagePlan,
          },
          hasPendingInvite && {
            key: 'resend',
            label: 'Resend invite',
            icon: Mail,
            onClick: () => resendMutation.mutate(),
            disabled: resendMutation.isPending,
          },
          isActive
            ? {
                key: 'suspend',
                label: 'Suspend',
                icon: Pause,
                onClick: () => {
                  setSuspendReason('')
                  setConfirmSuspend(true)
                },
              }
            : {
                key: 'reactivate',
                label: 'Reactivate',
                icon: Play,
                onClick: () => setConfirmReactivate(true),
              },
          tenant.status !== 'offboarded' && {
            key: 'offboard',
            label: 'Offboard',
            icon: Archive,
            onClick: () => {
              setOffboardReason('')
              setConfirmOffboard(true)
            },
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            danger: true,
            separatorBefore: true,
            onClick: () => setConfirmDelete(true),
          },
        ]}
      />

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Company"
        description="Update the company's display name, country and plan."
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setEditOpen(false)}
              disabled={editMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={editForm.handleSubmit((v) => editMutation.mutate(v))}
              loading={editMutation.isPending}
            >
              Save Changes
            </Button>
          </>
        }
      >
        <form
          onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
          className="space-y-4"
          noValidate
        >
          <TextField
            label="Company Name"
            required
            placeholder="Enter a Company Name"
            maxLength={200}
            error={editForm.formState.errors.name?.message}
            {...editForm.register('name')}
          />
          {/* Plan — Platform Admin can move the tenant onto a
           *  different plan from here. Private plans (sales-led
           *  Enterprise, etc.) are included with a "(Private)"
           *  suffix so the admin can tell them apart from public
           *  Sign-up plans. Save will only emit `planTier` in the
           *  payload when the selection actually changed. */}
          <div>
            <label
              htmlFor="tenant-plan-tier"
              className="block w-fit mb-1.5 text-sm font-medium text-[color:var(--color-ink-strong)]"
            >
              Plan
              <span
                aria-hidden="true"
                className="ml-0.5 text-[color:var(--color-priority-urgent)]"
              >
                *
              </span>
              <span className="sr-only">required</span>
            </label>
            <Select
              id="tenant-plan-tier"
              {...editForm.register('planTier')}
            >
              {/* A tenant with NO plan gets an explicit placeholder. Without
               *  it the browser shows whichever plan sorts first while the
               *  form value is still '', so saving could assign a plan the
               *  admin never chose. */}
              {!tenant.planTier ? (
                <option value="" disabled>
                  Select a Plan
                </option>
              ) : null}
              {/* Always include the tenant's CURRENT plan as the first
               *  option even if it's not in the active list — covers
               *  the case where the plan was deactivated after a
               *  tenant was assigned to it. */}
              {tenant.planTier &&
              !planOptions.some((o) => o.value === tenant.planTier) ? (
                <option value={tenant.planTier}>
                  {tenant.planName ?? 'No plan'} (current)
                </option>
              ) : null}
              {planOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {editForm.formState.errors.planTier?.message ? (
              <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {editForm.formState.errors.planTier.message}
              </p>
            ) : null}
          </div>
          {/* Country — lets an admin set or correct a company's location.
           *  Optional: a legacy company with no country can be left as-is.
           *  Full ISO list (matches the Create Company picker). */}
          <div>
            <label
              htmlFor="tenant-country"
              className="block w-fit mb-1.5 text-sm font-medium text-[color:var(--color-ink-strong)]"
            >
              Country
            </label>
            <CountryFilter
              id="tenant-country"
              fullWidth
              portal
              placeholder="Not set"
              clearLabel="Not set"
              clearValue=""
              value={editForm.watch('country') ?? ''}
              onChange={(v) =>
                editForm.setValue('country', v, { shouldValidate: true })
              }
            />
            {editForm.formState.errors.country?.message ? (
              <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {editForm.formState.errors.country.message}
              </p>
            ) : null}
          </div>
        </form>
      </Modal>

      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title="Manage Plan"
        description={`Move "${tenant.name}" onto a different plan. Plan caps and feature access update immediately.`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setPlanOpen(false)}
              disabled={planMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => planMutation.mutate()}
              loading={planMutation.isPending}
              disabled={
                planMutation.isPending ||
                !planTierDraft ||
                planTierDraft === tenant.planTier ||
                (isDowngrade && !confirmDowngrade)
              }
            >
              Save Plan
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Current plan — read-only context so the admin sees what
           *  they're moving the tenant off of before they pick a new one. */}
          <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2.5">
            <p className="text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)]">
              Current plan
            </p>
            <p className="mt-0.5 text-sm font-medium text-[color:var(--color-ink-strong)]">
              {tenant.planName ?? 'No plan selected'}
            </p>
          </div>
          <div>
            <label
              htmlFor="tenant-manage-plan"
              className="block w-fit mb-1.5 text-sm font-medium text-[color:var(--color-ink-strong)]"
            >
              New Plan
            </label>
            <Select
              id="tenant-manage-plan"
              value={planTierDraft}
              onChange={(e) => setPlanTierDraft(e.target.value)}
              disabled={plansQuery.isLoading}
            >
              {/* Keep the tenant's current plan selectable even if it was
               *  later deactivated/hidden in the catalogue. */}
              {tenant.planTier &&
              !planOptions.some((o) => o.value === tenant.planTier) ? (
                <option value={tenant.planTier}>
                  {tenant.planName ?? 'No plan'} (current)
                </option>
              ) : null}
              {planOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            {plansQuery.isLoading ? (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Loading plans…
              </p>
            ) : null}
          </div>
          {/* Downgrade guard — a paid or Enterprise tenant being moved
           *  onto a cheaper / trial plan can lose access to features
           *  and get pushed over the new plan's caps (member count,
           *  storage) with no warning. The banner spells that out and
           *  the checkbox forces the admin to acknowledge before Save
           *  enables (bug 0013115). */}
          {isDowngrade ? (
            <div className="rounded-lg border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] px-3 py-2.5 text-[color:var(--color-danger)]">
              <p className="text-sm font-semibold">
                This is a downgrade
              </p>
              {/* `break-words`/`overflow-wrap:anywhere` — the company name is
               *  interpolated here, and a long space-less one (a pasted
               *  "0000000000…") has no break opportunity, so it overflowed
               *  this paragraph and was clipped by the dialog's
               *  `overflow-hidden`. Matches what Modal's title/description
               *  and ConfirmDialog's message already do; this body copy was
               *  the one interpolation site still unprotected
               *  (QA, 2026-09-01). */}
              <p className="mt-0.5 text-xs break-words [overflow-wrap:anywhere]">
                Moving <span className="font-semibold">{tenant.name}</span> from{' '}
                <span className="font-semibold">
                  {currentPlan?.name ?? tenant.planName ?? 'the current plan'}
                </span>{' '}
                to{' '}
                <span className="font-semibold">
                  {draftPlan?.name ?? planTierDraft}
                </span>{' '}
                will apply the new plan&apos;s caps and feature access
                immediately. Members over the new cap stay signed in but
                can&apos;t take actions that exceed it; premium features
                turn off.
              </p>
              <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmDowngrade}
                  onChange={(e) => setConfirmDowngrade(e.target.checked)}
                  className="mt-0.5 accent-[color:var(--color-danger)] cursor-pointer"
                />
                <span>
                  I understand and want to downgrade this company.
                </span>
              </label>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Suspend dialog — captures a reason that's shown to locked-out
       *  members on the sign-in screen (with the support contact) so
       *  they know why access stopped and who to reach. */}
      <Modal
        open={confirmSuspend}
        onClose={() =>
          suspendMutation.isPending ? undefined : setConfirmSuspend(false)
        }
        title="Suspend Company"
        description={`Suspending "${tenant.name}" immediately signs every member out and blocks sign-in until you reactivate.`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmSuspend(false)}
              disabled={suspendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => suspendMutation.mutate()}
              loading={suspendMutation.isPending}
            >
              Suspend
            </Button>
          </>
        }
      >
        <div>
          <label
            htmlFor="tenant-suspend-reason"
            className="block w-fit mb-1.5 text-sm font-medium text-[color:var(--color-ink-strong)]"
          >
            Reason for suspension{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">
              (optional — shown to members)
            </span>
          </label>
          <textarea
            id="tenant-suspend-reason"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. Payment overdue — settle the latest invoice to restore access."
            className="w-full px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] resize-y"
          />
          <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]">
            Members will see this reason and the support email when they
            try to sign in.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmReactivate}
        title="Reactivate Company"
        message={`Reactivate "${tenant.name}"? Members will be able to sign in again.`}
        confirmLabel="Reactivate"
        variant="primary"
        confirming={reactivateMutation.isPending}
        onCancel={() => setConfirmReactivate(false)}
        onConfirm={() => reactivateMutation.mutate()}
      />

      {/* Offboard dialog — terminal action, same reason-capture pattern
       *  as Suspend so locked-out members see a coherent message on the
       *  sign-in screen. Distinct from Delete: data is archived, not
       *  removed, so an offboarded tenant can still be reactivated. */}
      <Modal
        open={confirmOffboard}
        onClose={() =>
          offboardMutation.isPending ? undefined : setConfirmOffboard(false)
        }
        title="Offboard Company"
        description={`Offboarding "${tenant.name}" archives the company, signs every member out and moves the row to the Offboarded bucket. Data is preserved — you can still reactivate later.`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmOffboard(false)}
              disabled={offboardMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => offboardMutation.mutate()}
              loading={offboardMutation.isPending}
            >
              Offboard
            </Button>
          </>
        }
      >
        <div>
          <label
            htmlFor="tenant-offboard-reason"
            className="block w-fit mb-1.5 text-sm font-medium text-[color:var(--color-ink-strong)]"
          >
            Reason for offboarding{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">
              (optional — shown to members)
            </span>
          </label>
          <textarea
            id="tenant-offboard-reason"
            value={offboardReason}
            onChange={(e) => setOffboardReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. Subscription ended — contact support to reopen the workspace."
            className="w-full px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] text-sm outline-none focus:border-[color:var(--color-accent)] resize-y"
          />
          <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]">
            Members will see this reason and the support email when they
            try to sign in.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Company"
        message={`Are you sure you want to delete "${tenant.name}"? This will permanently remove every Workspace, Space, List, Task, User, Report and File inside it. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />

      <Modal
        open={inviteResult !== null}
        onClose={() => setInviteResult(null)}
        title="Invitation Re-sent"
        description="The owner has been emailed a fresh password-setup link. You can also copy the link below as a fallback."
        size="md"
        footer={<Button onClick={() => setInviteResult(null)}>Done</Button>}
      >
        {inviteResult ? (
          <div className="space-y-3">
            <p className="text-sm text-[color:var(--color-ink)]">
              Sent to <strong>{inviteResult.owner?.emailId}</strong>.{' '}
              {inviteResult.setupLinkExpiresIn
                ? `The link expires in ${inviteResult.setupLinkExpiresIn} and can only be used once.`
                : 'The link can only be used once.'}
            </p>
            <CopyableUrl url={inviteResult.setupUrl} />
          </div>
        ) : null}
      </Modal>
    </>
  )
}

/** Copy-to-clipboard pill used by the create + resend-invite flows so
 *  the admin can hand the setup URL to the owner out-of-band. */
export function CopyableUrl({ url }) {
  // Tracks WHICH url was copied rather than a bare boolean. The label must
  // stay on "Copied" for the rest of the dialog (product decision: the
  // transient label was reported as the defect — a confirmation that erases
  // itself left the admin unsure whether the copy had happened at all). It
  // must still not claim "Copied" if this component is later handed a
  // different url, and deriving the flag from the value gets that for free,
  // with no reset effect. State resets anyway when the modal unmounts.
  const [copiedUrl, setCopiedUrl] = useState(null)
  const copied = copiedUrl === url
  async function copy() {
    // `copyText` falls back to a gesture-based `execCommand` copy when the
    // async Clipboard API is missing (non-secure origin) or REFUSED — the
    // latter reproduced on a real click on a focused HTTPS page with
    // `NotAllowedError: Write permission denied`, which left the label stuck
    // on "Copy" and the copy silently not happening (QA). See lib/clipboard.js.
    if (!(await copyText(url))) {
      toast.error('Could not copy link. Select the link and copy it manually.')
      return
    }
    setCopiedUrl(url)
    toast.success('Setup link copied to clipboard.')
  }
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 min-w-0 px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-xs text-[color:var(--color-ink)] break-all">
        {url}
      </code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:bg-[color:var(--color-card-hover)] text-sm cursor-pointer"
      >
        <Copy size={14} />
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
  )
}
